import { useState, useEffect } from 'react';
import './AdmissionsReport.css';

/**
 * Admissions Report Component
 * Statistics and analytics about admissions - applications, approvals, rejection rate
 */

function AdmissionsReport({ apiBase }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLevel, setFilterLevel] = useState('all');

  useEffect(() => {
    fetchApplications();
  }, [apiBase]);

  const fetchApplications = async () => {
    try {
      const response = await fetch(`${apiBase}/admin/admissions/applications`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        if (data.ok && Array.isArray(data.data)) {
          setApplications(data.data);
        }
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate statistics
  const stats = {
    total: applications.length,
    submitted: applications.filter(a => a.status_code === 'submitted').length,
    approved: applications.filter(a => a.status_code === 'approved').length,
    rejected: applications.filter(a => a.status_code === 'rejected').length,
    inReview: applications.filter(a => a.status_code === 'in_review').length,
  };

  const approvalRate = stats.total > 0 
    ? ((stats.approved / stats.total) * 100).toFixed(1)
    : 0;

  const filteredApplications = filterLevel === 'all'
    ? applications
    : applications.filter(a => a.level_code === filterLevel.toUpperCase());

  return (
    <div className="admissions-report">
      <div className="content-header">
        <h2 className="content-title">Admissions Report</h2>
        <p className="content-subtitle">Application statistics and analytics</p>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Applications</div>
          <div className="stat-value">{stats.total}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Submitted</div>
          <div className="stat-value">{stats.submitted}</div>
          <div className="stat-bar">
            <div className="stat-fill" style={{ width: `${(stats.submitted / stats.total * 100) || 0}%` }}></div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Approved</div>
          <div className="stat-value" style={{ color: '#28a745' }}>{stats.approved}</div>
          <div className="stat-bar">
            <div className="stat-fill" style={{ width: `${(stats.approved / stats.total * 100) || 0}%`, background: '#28a745' }}></div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Rejected</div>
          <div className="stat-value" style={{ color: '#dc3545' }}>{stats.rejected}</div>
          <div className="stat-bar">
            <div className="stat-fill" style={{ width: `${(stats.rejected / stats.total * 100) || 0}%`, background: '#dc3545' }}></div>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="metrics-container">
        <div className="metric-box">
          <div className="metric-label">Approval Rate</div>
          <div className="metric-value">{approvalRate}%</div>
        </div>
        <div className="metric-box">
          <div className="metric-label">Pending Review</div>
          <div className="metric-value">{stats.inReview}</div>
        </div>
      </div>

      {/* Applications by Level */}
      <div className="applications-section">
        <h3>Applications by Level</h3>
        
        <select 
          value={filterLevel} 
          onChange={(e) => setFilterLevel(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Levels</option>
          <option value="pg">Preschool (PG)</option>
          <option value="tk">Kindergarten (TK)</option>
          <option value="sd">Elementary (SD)</option>
          <option value="smp">Junior High (SMP)</option>
          <option value="sma">Senior High (SMA)</option>
        </select>

        {loading ? (
          <div className="loading">Loading applications...</div>
        ) : filteredApplications.length === 0 ? (
          <div className="empty-state">No applications found</div>
        ) : (
          <div className="applications-table">
            <table>
              <thead>
                <tr>
                  <th>Applicant</th>
                  <th>Level</th>
                  <th>Status</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {filteredApplications.slice(0, 10).map(app => (
                  <tr key={app.id}>
                    <td data-label="Applicant">-</td>
                    <td data-label="Level">{app.level_code}</td>
                    <td data-label="Status">
                      <span className={`status-badge status-${app.status_code}`}>
                        {app.status_code}
                      </span>
                    </td>
                    <td data-label="Submitted">
                      {new Date(app.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdmissionsReport;
